import { OrderedSet, Map } from 'immutable';

import * as urlQueryString from '../../../urlQueryString';
import {
  OWNER_PARAM_MAP,
  OWNER_PARAM_VALUE_MAP,
  FILTER_KEY_MAP,
  OS_PREFIX_MAP,
  OS_PREFIX_TO_PARAM_MAP,
  DEVICE_GROUP_ITEMS,
  FILTER_KEY_TO_PARAM_MAP,
  FILTER_KEYS,
} from './constants';

/**
 * Parse users collection to filter item
 *
 * @param {Object[]} users
 * @param {Boolean} isIndividualUserAllowed
 * @returns {Object[]}
 */
export const parseUsers = (users, isIndividualUserAllowed = true) => (
  users.reduce((parsedUsers, { username, id, teams }) => {
    if (isIndividualUserAllowed || teams?.length) {
      parsedUsers.push({
        name: id,
        label: username,
        type: 'user',
        ownerV1: username,
        teamId: teams?.[0]?.id,
      });
    }
    return parsedUsers;
  }, [])
);

/**
 * Parse teams collection to filter item
 *
 * @param {Object[]} teams
 * @returns {Object[]}
 */
export const parseTeams = (teams) => teams.map(({ name, id }) => ({
  name: id,
  label: name,
  type: 'team',
  ownerV1: id,
}));

/**
 * Deduplicate filter items
 * Removes duplicates by `name` and `type` values
 *
 * @param {Object} items
 * @returns {Immutable.OrderedSet}
 */
export const deduplicateItems = (items) => items.reduce((output, item) => {
  const hasItem = output.find(
    (value) => value.name === item.name && value.type === item.type,
  );

  if (!hasItem) {
    return output.add(item);
  }

  return output;
}, OrderedSet());

/**
 * Filter selected items by common values present in items
 *
 * @param {Immutable.OrderedSet} selected
 * @param {Immutable.OrderedSet} items
 * @returns {Immutable.OrderedSet}
 */
export const updateSelectedItems = (state, key) => {
  const selected = state.getIn(['filters', key, 'selectedItems']);
  const items = state.getIn(['filters', key, 'items']);

  return selected.filter((item) => items.has(item));
};

/**
 * Convert ownersV1 (multiple selection) to ownersV2 (single selection)
 * Find ownersV2 value based on the last element of ownersV1 value
 * @param {Object} queryParams
 * @param {Object} ownerParamMap
 * @param {Immutable.OrderedSet} ownersList
 * @returns {Object}
 */
export const convertOwnersV1ToV2 = (queryParams, ownerParamMap, ownersList) => {
  const ownersV2Key = Object.keys(queryParams).find((key) => (
    Object.prototype.hasOwnProperty.call(ownerParamMap, key)),
  );
  if (queryParams?.owners) {
    const selectedOwner = Array.isArray(queryParams.owners) ?
      queryParams.owners[queryParams.owners.length - 1] :
      queryParams.owners;
    const ownerItem = ownersList.find(
      ({ ownerV1 }) => ownerV1 === selectedOwner,
    );
    return ownerItem ?
      { [OWNER_PARAM_MAP[ownerItem.type]]: ownerItem.name } :
      null;
  }
  if (ownersV2Key) {
    return { [ownersV2Key]: queryParams[ownersV2Key] };
  }
  return null;
};

/**
 * Convert ownersV2 (single selection) to ownersV1 (multiple selection)
 * Replace last element of existing ownersV1 value
 * @param {Object} queryParams
 * @param {Immutable.OrderedSet} selectedOwners
 * @returns {Object}
 */
export const convertOwnersV2ToV1 = (queryParams, selectedOwners) => {
  const { owners: existingOwnersV1 = [] } = queryParams;
  if (!selectedOwners.size) {
    return existingOwnersV1;
  }
  const ownersV1 = Array.isArray(existingOwnersV1) ?
    [...existingOwnersV1] :
    [existingOwnersV1];
  ownersV1[ownersV1.length ? ownersV1.length - 1 : 0] = selectedOwners.first().ownerV1;

  return ownersV1;
};

const getMappedValues = (payload, mapping = {}) => {
  const key = mapping[payload.name] || payload.name;

  if (typeof key === 'function') {
    const mapFn = key;

    return {
      ...mapFn(payload.items.toJS()),
    };
  }

  return {
    [key]: payload.items.toJSON(),
  };
};

const getUpdatedValues = (payload, { state, mapping = {} }) => {
  const values = getMappedValues(payload, mapping);

  if (payload.name === 'owner' && state) {
    values.owners = convertOwnersV2ToV1(
      values,
      state.getIn(['filters', 'owner', 'selectedItems']),
    );
  }

  return values;
};

/**
 * Combine initial filters state with current location.search
 * and return them a result as object
 * @param {Immutable.Map} payload
 * @param {Object} location
 * @param {Object}
 * @returns {Object}
 */
export const setupFiltersQuery = (
  payload,
  location,
  { state, mapping = {} },
) => {
  const filters = payload.get('filters').reduce(
    (output, value, key) => ({
      ...output,
      ...getUpdatedValues(
        {
          name: key,
          items: value.get('selectedItems'),
        },
        { state, mapping },
      ),
    }),
    {},
  );

  const selectedDateFilter = payload.get('selectedDateFilter').toJS();

  return urlQueryString.set(location, {
    ...filters,
    dateFilter: JSON.stringify(selectedDateFilter),
  });
};

/**
 * Update current location.search with selected filters
 * and return result as object
 * @param {Object} payload
 * @param {Object} location
 * @param {Object}
 * @returns {Object}
 */
export const updateFiltersQuery = (
  payload,
  location,
  { state, mapping = {} },
) => urlQueryString.set(location, {
  ...getUpdatedValues(payload, { state, mapping }),
});

/**
 * Update current location.search with selected
 * date filter and return result as object
 * @param {Object} payload
 * @param {Object} location
 * @returns {Object}
 */
export const updateFiltersQueryWithDate = (payload, location) => (
  urlQueryString.set(location, { dateFilter: JSON.stringify(payload) })
);

/**
 * Remove filter key and value from current location.search
 * and return result as object
 * @param {Object} payload
 * @param {Object} location
 * @param {Object}
 * @returns {Object}
 */
export const removeFromFiltersQuery = (payload, location, { mapping = {} }) => {
  const key = mapping[payload.name] || payload.name;
  if (typeof key === 'function') {
    const mapFn = key;

    const mappedObject = mapFn([payload.value]);
    return urlQueryString.removeMany(location, Object.entries(mappedObject));
  }

  return urlQueryString.remove(location, key, payload.value);
};

/**
 * Clear all filter keys and values from current location.search
 * and return result as object
 * @param {Object} payload
 * @param {Object} location
 * @returns {Object}
 */
export const clearFiltersQuery = (payload, location) => {
  const query = urlQueryString.getAll(location);

  return Object.keys(query).reduce((output, key) => {
    if (
      payload.has(key) ||
      key in OWNER_PARAM_VALUE_MAP ||
      key === 'dateFilter' ||
      key === 'owners'
    ) {
      return output;
    }

    return {
      ...output,
      [key]: query[key],
    };
  }, {});
};

/**
 * Map owner object to query param
 * user type has higher priority than others because user type contains user_id and team_id
 * e.g. user object to object with `user_id` key
 *
 * @param {Object} owners
 * @returns {Object}
 */
export const mapOwnerToParams = (owners) => {
  if (!owners?.length) {
    return {};
  }

  const userOwner = owners.find(
    (owner) => OWNER_PARAM_MAP[owner.type] === OWNER_PARAM_MAP.user,
  );
  if (userOwner) {
    return {
      [OWNER_PARAM_MAP.owners]: userOwner.ownerV1,
      [OWNER_PARAM_MAP.user]: userOwner.name,
      ...(userOwner.teamId ?
        { [OWNER_PARAM_MAP.team]: userOwner.teamId } :
        null),
    };
  }

  const [owner] = owners;
  return {
    [OWNER_PARAM_MAP.owners]: owner.ownerV1,
    [OWNER_PARAM_MAP[owner.type]]: owner.name,
  };
};

/**
 * @param {string} key query param key
 * @param {array<{name: string, label: string}>} filterItems An array of the selected items for
 * this filter state.
 */
export const mapObjectFiltersToParams = (key) => (filterItems) => {
  if (!filterItems.length) {
    return {};
  }

  return {
    [key]: filterItems.map(({ name }) => name),
  };
};

/**
 * Returns myself option in owners filter for a given user
 * @param {Object} user
 * @returns {Object}
 */
export const getMyselfOwnerOption = (user) => ({
  name: user.user_id,
  label: `My Tests (${user.username})`,
  type: 'user',
  ownerV1: 'myself',
});

/**
 * Prepare list of OS values - os + os_version or os
 * @param {Object} payload
 * @returns {[]}
 */
export const parseOsList = (payload) => (payload?.versions?.length && OS_PREFIX_MAP[payload.name] ?
  payload.versions.map(
    ({ name }) => `${OS_PREFIX_MAP[payload.name]} ${name}`,
  ) :
  OS_PREFIX_MAP[payload.name] ?? payload.name);

/**
 * Map filter values to state object
 *
 * @param {Object} payload
 * @returns {Map}
 */
export const mapFiltersToState = (payload = {}) => (
  Object.entries(payload).reduce((output, [key, items]) => {
    const keyName = FILTER_KEY_MAP[key] || key;
    const isOs = keyName === FILTER_KEY_MAP.os;
    const itemMapping = isOs ? parseOsList : ({ name }) => name;
    return output.setIn([keyName, 'items'], OrderedSet(items.map(itemMapping)));
  }, Map())
);

/**
 * Map oses collection
 *
 * @param {Object[]} oses
 * @returns {Object[]}
 */
export const mapOsToParams = (oses) => ({
  os: oses.map((os) => OS_PREFIX_TO_PARAM_MAP[os] ?? os),
});

export const parseDateQueryParam = (_key, value, output) => {
  const selectedDateFilter = JSON.parse(value);

  if (selectedDateFilter.type === 'absolute') {
    selectedDateFilter.value = {
      startDate: new Date(selectedDateFilter.value.startDate),
      endDate: new Date(selectedDateFilter.value.endDate),
    };
  }

  return output.set('selectedDateFilter', Map(selectedDateFilter));
};

export const parseOwnerQueryParam = (
  key,
  value,
  output,
  { isUserSelected },
) => {
  const type = OWNER_PARAM_VALUE_MAP[key];
  const ownerValue = Array.isArray(value) ? value[0] : value;
  const owner = output
    .getIn(['filters', 'owner', 'items'])
    .find((item) => item.name === ownerValue && item.type === type);
  const isValidOwner = owner && (isUserSelected ? key === OWNER_PARAM_MAP.user : true);
  if (!isValidOwner) {
    return output;
  }

  return output.setIn(
    ['filters', 'owner', 'selectedItems'],
    OrderedSet([owner]),
  );
};

export const defaultParseQueryParam = (key, value, output) => {
  const filterKey = FILTER_KEY_MAP[key];

  if (!filterKey || !output.getIn(['filters', filterKey])) {
    return output;
  }

  const selectedItems = Array.isArray(value) ? value : [value];
  return output.setIn(
    ['filters', filterKey, 'selectedItems'],
    output.getIn(['filters', filterKey, 'selectedItems']).merge(selectedItems),
  );
};

export const parseLabelQueryParam = (options) => (key, value, output) => {
  const filterKey = FILTER_KEY_MAP[key];

  if (!filterKey || !output.getIn(['filters', filterKey])) {
    return output;
  }

  const selectedItemNames = Array.isArray(value) ? value : [value];
  const selectedItems = selectedItemNames
    .map((name) => options.find((option) => option.name === name))
    .filter(Boolean);
  return output.setIn(
    ['filters', filterKey, 'selectedItems'],
    output.getIn(['filters', filterKey, 'selectedItems']).merge(selectedItems),
  );
};

export const KEY_TO_PARSER_MAP = {
  ...Object.keys(OWNER_PARAM_VALUE_MAP).reduce(
    (ownerMap, key) => ({
      ...ownerMap,
      [key]: parseOwnerQueryParam,
    }),
    {},
  ),
  dateFilter: parseDateQueryParam,
  device_group: parseLabelQueryParam(DEVICE_GROUP_ITEMS),
};

export const FILTER_PARAMS_MAPPINGS = {
  ...FILTER_KEY_TO_PARAM_MAP,
  [FILTER_KEYS.OS]: mapOsToParams,
  [FILTER_KEYS.OWNER]: mapOwnerToParams,
  [FILTER_KEYS.DEVICE_GROUP]: mapObjectFiltersToParams(FILTER_KEY_TO_PARAM_MAP.deviceGroup),
};

export const FILTER_PARAMS_QUERY_MAPPINGS = {
  ...FILTER_KEY_TO_PARAM_MAP,
  [FILTER_KEYS.OS]: mapOsToParams,
  [FILTER_KEYS.OWNER]: mapOwnerToParams,
  [FILTER_KEYS.DEVICE_GROUP]: mapObjectFiltersToParams(FILTER_KEY_TO_PARAM_MAP.deviceGroup),
};
